// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Collapse, Form } from "react-bootstrap";

import {
  IAction,
  ActionType,
  raiseAction,
  IActionSortTable,
  IActionChangeRfid,
  IActionTypeConfirmPopup,
} from "actions";
import { getLocalDate, isSet } from "utils";
import { makeSortHeader, tabClickHandler } from "guiutils";
import { IState } from "states/states";
import { IOcppCharger, IRfid } from "states/ocppstates";
import { ITableSort } from "states/guistates";
import Rfid from "model/rfid";

import { ReactComponent as IconEdit } from "static/iconEdit.svg";
import { ReactComponent as IconDelete } from "static/iconDelete.svg";
import { ReactComponent as IconExpandMore } from "static/iconExpandMore.svg";
import { ReactComponent as IconExpandLess } from "static/iconExpandLess.svg";

import ModalRfidSettings from "view/modal/ModalRfidSettings";
import ModalRfidLink from "view/modal/ModalRfidLink";
import SearchFilter from "view/comp/SearchFilter";
import FormattedDate from "./FormattedDate";

// sort key of table
const TABLE_NAME = "t_rfids";
// RFID card number limit to get details
// keep consistent with api/listRfids.ts
const MAX_RFID_CARDS_FOR_DETAILS = 150;

interface IRfidsProps {
  siteId: number;
  rfids: IRfid[];
  ocppChargers: IOcppCharger[];
  isReadOnly: boolean;
  isExtendedDataLoaded: boolean;
  tableSort: ITableSort | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: IRfidsProps = {
    siteId: state.cached.selectedSite!,
    rfids: state.ocpp.rfid,
    ocppChargers: state.ocpp.charger,
    isReadOnly: false,
    isExtendedDataLoaded: isSet(state.scheduler.ocppRfidListExt.lastData),
    tableSort: state.gui.tableSort[TABLE_NAME],
  };
  return props;
};

interface IRfidsState {
  isExpanded: boolean;
  showAll: boolean;
  filterText: string;
}

interface IRfidsDispatch {
  onSort: (sort: IActionSortTable) => void;
  onEdit: (id: number) => void;
  onNewRfid: () => void;
  onConfirmDelete: (id: number, title: string, message: string) => void;
  onDeleteRfid: (id: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IRfidsDispatch = {
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
    onEdit: (id: number) => {
      dispatch(raiseAction(ActionType.ACT_EDIT_RFID, id));
    },
    onNewRfid: () => {
      dispatch(raiseAction(ActionType.ACT_EDIT_RFID, undefined));
    },
    onConfirmDelete: (id: number, title: string, message: string) => {
      const confirmPayload: IActionChangeRfid = {
        id,
        rfid: undefined,
      };
      const payload: IActionTypeConfirmPopup = {
        title,
        message,
        action: ActionType.ACT_SAVE_OCPP_RFID_REQ,
        actionPayload: confirmPayload,
      };
      dispatch(raiseAction(ActionType.ACT_CONFIRM_POPUP, payload));
    },
    onDeleteRfid(id: number) {
      const payload: IActionChangeRfid = {
        id,
        rfid: undefined,
      };
      dispatch(raiseAction(ActionType.ACT_SAVE_OCPP_RFID_REQ, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msgEdit: {
    id: "edit charging card",
  },
  msgLink: {
    id: "link charging card",
  },
  msgDelete: {
    id: "delete charging card",
  },
  msgUsedLimit: {
    id: "the energy and cost limits exclude ongoing charging sessions",
  },
  confirmDeleteTitle: {
    id: "Deleting charging card",
  },
  confirmDeleteBody: {
    id: "This will delete this charging card. The charging data itself will still be available. Are you sure?",
  },
  confirmDeleteInactive: {
    id: "This will delete all inactive charging cards of the list. Are you sure?",
  },
  placeholderSearchField: {
    id: "Search for cards...",
  },
  tootTipSearchField: {
    id: "Search for cards-numbers, names or categories. To search for multiple fields, separate search texts by a space!",
  },
  msgRfidLimitExceeded: {
    id: "The number of active charging cards exceeds the limit. Please delete some cards to be able to create new cards.",
  },
});

class RfidsComponent extends React.Component<
  IRfidsProps & IRfidsDispatch & WrappedComponentProps,
  IRfidsState
> {
  state: IRfidsState = {
    isExpanded: true,
    showAll: false,
    filterText: "",
  };

  private getSortKey(a: number, b: number) {
    const sortCol = (this.props.tableSort?.column ?? "rfid") as keyof IRfid;
    let keyA = this.props.rfids[a]?.[sortCol!];
    let keyB = this.props.rfids[b]?.[sortCol!];
    // special handling for special columns
    if (sortCol === "limitEnd") {
      // column is used for all restrictions; just sum then up, even if not necessarily correct
      keyA =
        (this.props.rfids[a].limitStart ?? 0) +
        (this.props.rfids[a].limitEnd ?? 0) +
        (this.props.rfids[a].limitEnergy ?? 0);
      keyB =
        (this.props.rfids[b].limitStart ?? 0) +
        (this.props.rfids[b].limitEnd ?? 0) +
        (this.props.rfids[b].limitEnergy ?? 0);
    } else if (sortCol === "limitEnergy") {
      keyA =
        (this.props.rfids[a].limitEnergy ?? this.props.rfids[a].limitCost ?? 0) * 10000 +
        (this.props.rfids[a].usedEnergy ?? this.props.rfids[a].usedCost ?? 0);
      keyB =
        (this.props.rfids[b].limitEnergy ?? this.props.rfids[b].limitCost ?? 0) * 10000 +
        (this.props.rfids[b].usedEnergy ?? this.props.rfids[b].usedCost ?? 0);
    }
    const sort = isSet(keyA) && isSet(keyB) ? (keyA > keyB ? 1 : -1) : isSet(keyB) ? 1 : -1;
    return this.props.tableSort?.isSortAscending !== false ? sort : -sort;
  }

  public render() {
    const filterTextsLowerCase = this.state.filterText.toLocaleLowerCase().split(" ");
    const tmpRfidItems = Array.from(Array(this.props.rfids.length).keys())
      .filter((i) => {
        const curRfid = this.props.rfids[i];
        return (
          curRfid.isActive &&
          (this.state.showAll || !new Rfid(curRfid).isExpired()) &&
          (this.state.filterText === "" ||
            !filterTextsLowerCase.some(
              (filterTextLowerCase) =>
                filterTextLowerCase !== "" &&
                !curRfid.rfid.toLocaleLowerCase().includes(filterTextLowerCase) &&
                !curRfid.name?.toLocaleLowerCase().includes(filterTextLowerCase) &&
                !curRfid.category?.toLocaleLowerCase().includes(filterTextLowerCase),
            ))
        );
      })
      .sort((a, b) => this.getSortKey(a, b));

    const showCategory = tmpRfidItems.some((item) => {
      const rfid = this.props.rfids[item];
      return isSet(rfid.category) && rfid.category !== "";
    });

    const rfidItems = tmpRfidItems.map((curItem) => {
      return this.renderRfid(curItem, showCategory);
    });

    const isRfidCardNumberRestricted =
      this.props.rfids.filter(
        (curRfid) => curRfid.isActive && (curRfid.limitStart || curRfid.limitPeriod),
      ).length > MAX_RFID_CARDS_FOR_DETAILS;

    return (
      <div id="rfids" className="mt-1">
        {!this.props.isReadOnly && this.state.isExpanded && this.renderListHeader()}
        <h1
          className="link"
          onClick={() => {
            this.setState({ isExpanded: !this.state.isExpanded });
          }}
        >
          <Button
            variant="link"
            className="expand-button me-2 d-print-none"
            disabled={isRfidCardNumberRestricted}
            title={
              isRfidCardNumberRestricted
                ? this.props.intl.formatMessage(messages.msgRfidLimitExceeded)
                : undefined
            }
          >
            {this.state.isExpanded ? <IconExpandMore /> : <IconExpandLess />}
          </Button>
          <FormattedMessage id="Charging Cards" />
        </h1>
        <Collapse in={this.state.isExpanded}>
          <div id="rfidtable">
            {rfidItems.length > 0 ? (
              <>
                <table className="datatable fullwidthwhite">
                  {this.renderTableHeader(showCategory)}
                  <tbody>{rfidItems}</tbody>
                </table>
                {!this.props.isExtendedDataLoaded && (
                  <div className="mt-2 error">
                    {isRfidCardNumberRestricted ? (
                      <>
                        <FormattedMessage id="Too many active charging cards (>150)." />{" "}
                        <FormattedMessage id="Displaying of additional details and editing of charging cards is not possible." />
                      </>
                    ) : (
                      <>
                        <FormattedMessage id="Card data not fully loaded yet." />{" "}
                        <FormattedMessage id="If this persists, please reduce the number of active charging cards." />
                      </>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div style={{ paddingTop: 5, paddingBottom: 5 }}>
                <FormattedMessage id="No charging cards added yet." />
              </div>
            )}
          </div>
        </Collapse>
        <ModalRfidSettings />
        <ModalRfidLink />
      </div>
    );
  }

  private renderListHeader() {
    return (
      <div id="rfid-buttonbox">
        <SearchFilter
          className="tableSearchBox d-lg-inline-flex d-none"
          filterText={this.state.filterText}
          placeholder={this.props.intl.formatMessage(messages.placeholderSearchField)}
          tooltip={this.props.intl.formatMessage(messages.tootTipSearchField)}
          onChange={(filterText) => {
            this.setState({ filterText });
          }}
        />
        <div className="me-3 link" style={{ marginTop: 11 }}>
          <Form.Check
            id="showAll"
            className="me-2"
            style={{ marginTop: 3 }}
            checked={this.state.showAll}
            onChange={(event) => {
              this.setState({
                showAll: event?.currentTarget.checked,
              });
            }}
            label={<FormattedMessage id="Show Inactive" />}
          />
        </div>
        <Button
          id="newrfid"
          variant="secondary"
          className="mt-1 mb-1"
          onClick={(e) => this.props.onNewRfid()}
        >
          <FormattedMessage id="New Charging Card" />
        </Button>
        {false &&
          this.props.rfids.filter((curItem) => new Rfid(curItem).isExpired()).length > 0 && (
            <Button
              id="deleterfids"
              variant="secondary"
              className="mt-1 mb-1 ms-3"
              onClick={(e) => this.deleteInactiveRfids()}
            >
              <FormattedMessage id="Delete Inactive Cards" />
            </Button>
          )}
      </div>
    );
  }

  // this function is just a short-cut to reduce boilerplate
  private makeSortHeader(
    column: keyof IRfid,
    caption: JSX.Element,
    classes?: string[],
    title?: string,
    colSpan?: number,
  ) {
    return makeSortHeader(
      TABLE_NAME,
      column,
      caption,
      this.props.tableSort ?? { column: "rfid", isSortAscending: true },
      this.props.onSort,
      classes,
      title,
      colSpan,
    );
  }

  private renderTableHeader(showCategory: boolean) {
    return (
      <thead>
        <tr>
          {this.makeSortHeader("rfid", <FormattedMessage id="Card Number" />)}
          {showCategory === true &&
            this.makeSortHeader("category", <FormattedMessage id="Category" />)}
          {this.makeSortHeader("name", <FormattedMessage id="Name" />)}
          {this.props.isExtendedDataLoaded &&
            this.makeSortHeader("lastUsage", <FormattedMessage id="Last Usage" />, ["right"])}
          {this.props.isExtendedDataLoaded &&
            this.makeSortHeader("chargers", <FormattedMessage id="Chargers" />, ["optional"])}
          {this.makeSortHeader("limitEnd", <FormattedMessage id="Validity" />, ["optional"])}
          {this.makeSortHeader("limitEnergy", <FormattedMessage id="Energy Limit" />, [
            "optional",
            "right",
          ])}
          {!this.props.isReadOnly && <th></th> /* Edit-Icon */}
          {!this.props.isReadOnly && <th></th> /* Link-Icon */}
        </tr>
      </thead>
    );
  }

  private renderRfid(index: number, showCategory: boolean) {
    const rfid = this.props.rfids[index];
    const chargers = (rfid.chargers ?? []).map(
      (curItem) =>
        this.props.ocppChargers.find((curCharger) => curCharger.fk_charger === curItem)
          ?.chargepoint,
    );
    const timeRestriction =
      rfid.limitStart || rfid.limitEnd
        ? `${rfid.limitStart ? getLocalDate(rfid.limitStart * 1000) : ""} - ${
            rfid.limitEnd ? getLocalDate(rfid.limitEnd * 1000) : ""
          }`
        : undefined;
    const energyRestriction: string[] = [];
    if (rfid.limitEnergy) {
      energyRestriction.push(
        `${isSet(rfid.usedEnergy) ? `${Math.round(rfid.usedEnergy)} / ` : ""}${
          rfid.limitEnergy
        } kWh`,
      );
    }
    if (rfid.limitCost) {
      energyRestriction.push(
        `${isSet(rfid.usedCost) ? `${Math.round(rfid.usedCost)} / ` : ""}${rfid.limitCost} EUR`,
      );
    }
    const isExpired = new Rfid(rfid).isExpired();

    return (
      <tr key={index} className={!rfid.isActive || isExpired ? "deactivated" : undefined}>
        <td>{rfid.rfid}</td>
        {showCategory === true && <td>{rfid.category}</td>}
        <td>{rfid.name}</td>
        {this.props.isExtendedDataLoaded && (
          <td className="right">
            {isSet(rfid.lastUsage) && (
              <FormattedDate valueMs={rfid.lastUsage * 1000} doZeroPadding={true} />
            )}
          </td>
        )}
        {this.props.isExtendedDataLoaded && (
          <td className="optional">
            {rfid.isActive &&
              (rfid.chargerRestricted ? (
                chargers.join(", ")
              ) : (
                <FormattedMessage id="all chargers" />
              ))}
          </td>
        )}
        <td className="optional">{rfid.isActive ? timeRestriction : <></>}</td>
        <td
          className="optional right"
          title={
            rfid.limitEnergy || rfid.limitCost
              ? this.props.intl.formatMessage(messages.msgUsedLimit)
              : undefined
          }
        >
          {rfid.isActive ? energyRestriction.join(", ") : <></>}
        </td>
        {!this.props.isReadOnly && (
          <td className="edit-col img-link nowrap nrp">
            {this.props.isExtendedDataLoaded ? (
              <span
                tabIndex={0}
                onKeyDown={tabClickHandler}
                onClick={(_evt) => this.props.onEdit(rfid.id)}
                title={this.props.intl.formatMessage(messages.msgEdit)}
              >
                <IconEdit />
              </span>
            ) : (
              <span
                className="ms-1"
                tabIndex={0}
                onKeyDown={tabClickHandler}
                onClick={(_evt) =>
                  this.props.onConfirmDelete(
                    rfid.id,
                    this.props.intl.formatMessage(messages.confirmDeleteTitle),
                    this.props.intl.formatMessage(messages.confirmDeleteBody),
                  )
                }
                title={this.props.intl.formatMessage(messages.msgDelete)}
              >
                <IconDelete />
              </span>
            )}
          </td>
        )}
      </tr>
    );
  }

  private deleteInactiveRfids() {
    if (confirm(this.props.intl.formatMessage(messages.confirmDeleteInactive))) {
      this.props.rfids
        .filter((curItem) => new Rfid(curItem).isExpired())
        .forEach((curItem) => {
          this.props.onDeleteRfid(curItem.id);
        });
    }
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(RfidsComponent));
